import { Link } from 'react-router-dom';
import { LogoMark } from './LogoMark';
import { LOGIN_URL } from '../../lib/links';
import * as copy from '../../content/site';
import { SOLUTION_GROUPS, SOLUTIONS_MENU } from '../../content/solutions/nav';

const productLinks = [
  { name: 'Product', to: '/product', testId: 'footer-link-overview' },
  { name: 'AI Features', to: '/ai-features', testId: 'footer-link-ai-features' },
  { name: 'Security', to: '/security', testId: 'footer-link-security' },
  { name: 'How It Works', to: '/how-it-works', testId: 'footer-link-how-it-works' },
  { name: 'Use Cases', to: '/use-cases', testId: 'footer-link-use-cases' },
];

// All nine solution pages, by role then by deal type.
const solutionLinks = SOLUTION_GROUPS.flatMap((group) => group.links);

const supportLinks = [
  { name: 'Contact Support', to: '/support', testId: 'footer-link-support' },
  { name: 'System Status', to: '/status', testId: 'footer-link-status' },
];

const legalLinks = [
  { name: 'Privacy Policy', to: '/privacy', testId: 'footer-link-privacy' },
  { name: 'Terms of Use', to: '/terms', testId: 'footer-link-terms' },
];

const linkClass = 'text-sm text-primary-200 hover:text-white transition-colors duration-150';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary-900 text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-12">
          <div className="col-span-2 md:col-span-4 lg:col-span-3">
            <div className="flex items-center gap-3 mb-4">
              <LogoMark className="h-9 w-auto text-white" />
              <span className="text-lg font-semibold">DiligensHub</span>
            </div>
            <p className="text-primary-200 text-sm leading-relaxed max-w-sm">
              {copy.footer.tagline}
            </p>
          </div>

          <div className="lg:col-span-2">
            <h2 className="label-uppercase text-primary-300 mb-4">Product</h2>
            <ul className="space-y-2.5">
              {productLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={linkClass} data-testid={link.testId}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h2 className="label-uppercase text-primary-300 mb-4">{SOLUTIONS_MENU}</h2>
            <ul className="space-y-2.5">
              {solutionLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={linkClass} data-testid={`footer-link-${link.to.split('/').pop()}`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h2 className="label-uppercase text-primary-300 mb-4">Access</h2>
            <ul className="space-y-2.5">
              <li>
                <Link to="/request-info" className={linkClass} data-testid="footer-link-request-info">
                  Request Information
                </Link>
              </li>
              <li>
                <a href={LOGIN_URL} className={linkClass} data-testid="footer-link-login">
                  Log In
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h2 className="label-uppercase text-primary-300 mb-4">Support</h2>
            <ul className="space-y-2.5">
              {supportLinks.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className={linkClass} data-testid={link.testId}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-primary-800 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-primary-300">
            © {currentYear} DiligensHub. All rights reserved.
          </p>
          <nav aria-label="Legal" className="flex gap-6">
            {legalLinks.map((link) => (
              <Link key={link.to} to={link.to} className={linkClass} data-testid={link.testId}>
                {link.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
